// PDKS (parmak / yüz terminalleri) sayfası kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'pdks.management';

export const pdksManagementGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'PDKS terminalleri nasıl yönetilir?'),
  intro: t(`${G}.intro`, 'Parmak izi, yüz ve kart okuyan personel devam (PDKS) terminallerini izler, personelin cihazlara yazılmasını ve okutmaların puantaja işlenmesini takip edersiniz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  navItems: [
    t(`${G}.nav.devices`, 'Cihazlar'),
    t(`${G}.nav.logs`, 'Kayıtlar'),
    t(`${G}.nav.biometrics`, 'Biyometri'),
    t(`${G}.nav.settings`, 'Ayarlar'),
  ],
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık ve özet kartları'),
    tabs: t(`${G}.regions.tabs`, 'Sekmeler'),
    body: t(`${G}.regions.body`, 'Sekme içeriği'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Terminali sisteme bağlayın'),
      text: t(`${G}.s1.text`, 'Terminal bu sayfadan eklenmez. Cihaz Yönetimi\'nden PDKS ekranı oluşturup üretilen kodu terminallerin bağlı olduğu bilgisayardaki local-service\'e girin; agent terminali kendisi bildirir ve Cihazlar sekmesinde görünür. Özet kartları Agent, Cihaz ve Bekleyen atama sayısını gösterir.'),
      tips: [
        t(`${G}.s1.tip1`, '"PDKS tabloları bu şirkette henüz açılmamış" uyarısı çıkarsa destek ekibine başvurun.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s2.title`, '2. Cihazlar: durum, personel ve düzenleme'),
      text: t(`${G}.s2.text`, 'Her cihazın marka, IP, son görülme, personel sayısı ve senkron durumu listelenir: "Uyumlu" tüm personel cihaza yazılmış, "Bekleyen" yazılmayı bekleyen personel var demektir. Kişi simgesi cihazdaki personeli ve senkron durumlarını gösterir; çöp kutusu kişiyi cihazdan kaldırmayı kuyruğa alır.'),
      tips: [
        t(`${G}.s2.tip1`, 'Personeli cihaza atamak ve kart seçmek Kartlar ekranında yapılır: personele kart tanımlarken PDKS terminallerini seçersiniz. "Kartlara git" o ekranı açar.'),
        t(`${G}.s2.tip2`, 'Kalem simgesi: Ad, Konum, Yön modu (Cihaz bildirir / Sabit / Sırayla türet), UTC farkı, cihaz kullanıcı adı ve parolası (boş bırakılırsa değişmez) ve "Senkron açık".'),
        t(`${G}.s2.tip3`, 'Yön cihazdan gelmezse sırayla türetilir: günün ilk hareketi giriş, sonraki çıkış sayılır.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s3.title`, '3. Kayıtlar: okutmalar işlendi mi?'),
      text: t(`${G}.s3.text`, 'Terminalden gelen her okutma ham kayıt olarak listelenir: cihaz saati, cihaz kullanıcısı, yön ve durum. "İşlendi" okutmanın personelin vardiya puantajına yazıldığını gösterir; "Punch" sütununda oluşan puantaj kaydı görünür. Cihazlar sekmesinde bir cihaz seçiliyse yalnız onun kayıtları listelenir.'),
      tips: [
        t(`${G}.s3.tip1`, 'Atlandı: cihaz kullanıcısı bir personele eşlenemedi ("eşlenmedi"), olay puantaj üretmiyor veya kısa sürede tekrar okutuldu. Nedeni "Not" sütununda yazar.'),
        t(`${G}.s3.tip2`, 'Hata: kayıt işlenemedi; nedeni "Not" sütununda yazar.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Biyometri: rıza olmadan yüz kaydı yok'),
      text: t(`${G}.s4.text`, 'Yüz şablonu özel nitelikli kişisel veridir. Yalnızca açık rızası olan personele toplanır ve aynı marka cihazlara kopyalanır. "Rıza kaydet" ile personelin rızasını kaydedin; "Rızayı iptal" şablonları buluttan siler ve cihazlardan yüzün temizlenmesini ister.'),
      tips: [
        t(`${G}.s4.tip1`, 'Biyometri Ayarlar sekmesinde kapalıysa rıza kaydedilebilir ama şablon toplanmaz ve cihazlara yazılmaz.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Ayarlar'),
      text: t(`${G}.s5.text`, 'Tekrar okutma eşiği (sn): bu süre içinde aynı kişinin tekrar okutması yok sayılır. Biyometri açık (KVKK opt-in), Şablon saklama (gün, 0 = süresiz) ve Geçiş fotoğrafını sakla seçenekleri de buradadır. Değişikliklerden sonra "Kaydet"e basın.'),
      tips: [
        t(`${G}.s5.tip1`, 'Asıl puantaj modu vardiya tanımındadır (Vardiyalar ekranı: Tek QR / Çift QR). "Yedek puantaj modu" şirket genelini değiştirmez; yalnızca henüz vardiyaya atanmamış personele uygulanır, vardiyası olanlarda yok sayılır.'),
        t(`${G}.s5.tip2`, 'Çift QR: her okutma ayrı giriş/çıkış satırıdır. Tek QR: günde tek satır (giriş + çıkış).'),
        t(`${G}.s5.tip3`, 'Başlıktaki (i) düğmesi bu rehberi her zaman yeniden açar; yenile düğmesi açık sekmeyi yeniden yükler.'),
      ],
    },
  ],
});
